import apiClient from './client';

export interface Department {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  code?: string;
  parentDepartmentId?: string;
  parentDepartmentName?: string;
  headUserId?: string;
  headUserName?: string;
  headUserEmail?: string;
  isActive: boolean;
  settings?: Record<string, any>;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface DepartmentMember {
  id: string;
  cognitoUserId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  status: string;
  departmentRole: string;
  joinedAt: string;
}

export interface ListDepartmentsParams {
  includeInactive?: boolean;
  parentId?: string;
  search?: string;
}

export interface CreateDepartmentInput {
  name: string;
  description?: string;
  code?: string;
  parentDepartmentId?: string;
  headUserId?: string;
}

export interface UpdateDepartmentInput {
  name?: string;
  description?: string;
  code?: string;
  parentDepartmentId?: string | null;
  headUserId?: string | null;
  isActive?: boolean;
  settings?: Record<string, any>;
}

export interface AssignUserInput {
  userId: string;
  departmentRole?: 'head' | 'manager' | 'member';
}

class DepartmentsService {
  async listDepartments(params: ListDepartmentsParams = {}): Promise<{ departments: Department[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params.includeInactive) queryParams.append('includeInactive', 'true');
    if (params.parentId) queryParams.append('parentId', params.parentId);
    if (params.search) queryParams.append('search', params.search);

    const response = await apiClient.get(`/departments?${queryParams.toString()}`);
    // API returns { success: true, data: { departments } }
    const departments = response.data?.data?.departments || [];
    return { departments, total: departments.length };
  }

  async getDepartment(id: string): Promise<Department> {
    const response = await apiClient.get(`/departments/${id}`);
    // API returns { success: true, data: Department }
    return response.data?.data || response.data;
  }

  async createDepartment(input: CreateDepartmentInput): Promise<Department> {
    const response = await apiClient.post('/departments', input);
    // API returns { success: true, data: Department }
    return response.data?.data || response.data;
  }

  async updateDepartment(id: string, input: UpdateDepartmentInput): Promise<Department> {
    const response = await apiClient.put(`/departments/${id}`, input);
    // API returns { success: true, data: Department }
    return response.data?.data || response.data;
  }

  async deleteDepartment(id: string): Promise<void> {
    await apiClient.delete(`/departments/${id}`);
  }

  async getDepartmentMembers(id: string): Promise<{ members: DepartmentMember[] }> {
    const response = await apiClient.get(`/departments/${id}/members`);
    // API returns { success: true, data: { members } }
    return response.data?.data || { members: [] };
  }

  async assignUser(departmentId: string, input: AssignUserInput): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/departments/${departmentId}/members`, input);
    return response.data;
  }

  async removeUser(departmentId: string, userId: string): Promise<void> {
    await apiClient.delete(`/departments/${departmentId}/members/${userId}`);
  }
}

export const departmentsService = new DepartmentsService();
